import * as React from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router' 
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Save, X, Info, Settings, Layers, FileJson } from "lucide-react";
import { toast } from "sonner";
import { getCategoryLabel, getPurposeLabel, getSubPurposeLabel } from "@genieartinc/schemas";
import { SummaryTab } from "@/components/model-management/detail/summary-tab";
import { MetadataTab } from "@/components/model-management/detail/metadata-tab";
import { ParametersTab } from "@/components/model-management/detail/parameters-tab";
import { JsonSchemaTab } from "@/components/model-management/detail/json-schema-tab";
import type { AdminParameterOverride } from "@genieartinc/schemas";
import type { SchemaProperty } from "@/components/model-management/detail/parameters-tab";

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

interface ModelFamily {
  id: string;
  slug: string;
  name: string;
}

interface Provider {
  id: string;
  key: string;
  name: string;
}

interface AiModel {
  id: string;
  name: string;
  endpointId: string;
  description?: string;
  originalTitle?: string;
  originalDescription?: string;
  providerId: string;
  category: string;
  nodeType: string;
  modelFamilyId?: string;
  modelFamily?: { id: string; slug: string; name: string };
  purpose?: string;
  subPurpose?: string;
  status?: string;
  enabled: boolean;
  isDefault?: boolean;
  featuredScore: number;
  version?: string;
  openApiEndpointId?: string;
  capabilities?: string[];
  settings?: {
    basic?: string[];
    style?: string[];
    advanced?: string[];
  };
  pricing?: {
    originalPrice?: number;
    currency?: string;
    unit?: string;
  };
  parameterSchema?: JsonValue;
  parameterOverrides?: JsonValue;
  createdAt: string;
  updatedAt: string;
  lastSyncedAt?: string;
}

export const Route = createFileRoute('/dashboard/model-management/$')({
  component: ModelDetailPage,
})

function ModelDetailPage() {
  const navigate = useNavigate();
  const { _splat: modelId } = Route.useParams();

  const [model, setModel] = React.useState<AiModel | null>(null);
  const [modelFamilies, setModelFamilies] = React.useState<ModelFamily[]>([]);
  const [providers, setProviders] = React.useState<Provider[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [showCancelDialog, setShowCancelDialog] = React.useState(false);

  // Form state
  const [enabled, setEnabled] = React.useState(false);
  const [featuredScore, setFeaturedScore] = React.useState(0);
  const [parameterSchema, setParameterSchema] = React.useState<string>("");
  const [parameterOverrides, setParameterOverrides] = React.useState<Record<string, AdminParameterOverride | JsonValue>>({});
  const [activeTab, setActiveTab] = React.useState("summary");

  // Metadata fields
  const [modelTitle, setModelTitle] = React.useState<string>("");
  const [modelDescription, setModelDescription] = React.useState<string>("");
  const [selectedNodeType, setSelectedNodeType] = React.useState<string>("");
  const [selectedCategory, setSelectedCategory] = React.useState<string>("");
  const [selectedModelFamilyId, setSelectedModelFamilyId] = React.useState<string>("none");
  const [selectedPurpose, setSelectedPurpose] = React.useState<string>("none");
  const [selectedSubPurpose, setSelectedSubPurpose] = React.useState<string>("none");


  // Track form changes
  React.useEffect(() => {
    if (!model) return;

    const changed =
      enabled !== model.enabled ||
      featuredScore !== model.featuredScore ||
      modelTitle !== model.name ||
      modelDescription !== (model.description || "") ||
      selectedNodeType !== model.nodeType ||
      selectedCategory !== model.category ||
      selectedModelFamilyId !== (model.modelFamilyId || "none") ||
      selectedPurpose !== (model.purpose || "none") ||
      selectedSubPurpose !== (model.subPurpose || "none") ||
      parameterSchema !== JSON.stringify(model.parameterSchema, null, 2) ||
      JSON.stringify(parameterOverrides) !== JSON.stringify(model.parameterOverrides || {});

    setHasChanges(changed);
  }, [
    model,
    enabled,
    featuredScore,
    modelTitle,
    modelDescription,
    selectedNodeType,
    selectedCategory,
    selectedModelFamilyId,
    selectedPurpose,
    selectedSubPurpose,
    parameterSchema,
    parameterOverrides,
  ]);

  // Fetch model families and providers
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [familiesResponse, providersResponse] = await Promise.all([
          fetch(`${API_ADMIN_BASE_URL}/api/admin/model-families`, { credentials: "include" }),
          fetch(`${API_ADMIN_BASE_URL}/api/admin/ai-providers`, { credentials: "include" }),
        ]);

        if (!familiesResponse.ok) throw new Error("Failed to fetch model families");
        if (!providersResponse.ok) throw new Error("Failed to fetch providers");

        const familiesResult = await familiesResponse.json();
        const providersResult = await providersResponse.json();

        setModelFamilies(familiesResult.data.modelFamilies || []);
        setProviders(providersResult.data.providers || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Fetch model data
  React.useEffect(() => {
    if (!modelId) return;

    const fetchModel = async () => {
      try {
        const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/model-management/${encodeURIComponent(modelId)}`, {
          credentials: "include",
        });

        if (!response.ok) throw new Error("Failed to fetch model");

        const result = await response.json();
        const modelData = result.data.model;
        setModel(modelData);
        setEnabled(modelData.enabled);
        setFeaturedScore(modelData.featuredScore || 0);
        setParameterSchema(
          modelData.parameterSchema
            ? JSON.stringify(modelData.parameterSchema, null, 2)
            : "{}"
        );
        setParameterOverrides(modelData.parameterOverrides || {});

        // Initialize metadata fields
        setModelTitle(modelData.name || "");
        setModelDescription(modelData.description || "");
        setSelectedNodeType(modelData.nodeType || "");
        setSelectedCategory(modelData.category || "");
        setSelectedModelFamilyId(modelData.modelFamilyId || "none");
        setSelectedPurpose(modelData.purpose || "none");
        setSelectedSubPurpose(modelData.subPurpose || "none");

        document.title = `${modelData.name} - Model Management - Admin`;
      } catch (error) {
        console.error("Error fetching model:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchModel();
  }, [modelId]);

  const handleSave = async () => {
    if (!model || saving) return;

    setSaving(true);
    try {
      let parsedSchema = model.parameterSchema;
      if (activeTab === "json" && parameterSchema !== JSON.stringify(model.parameterSchema, null, 2)) {
        try {
          parsedSchema = JSON.parse(parameterSchema);
        } catch {
          toast.error("Invalid JSON in parameter schema. Please fix before saving.");
          setSaving(false);
          return;
        }
      }

      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/model-management/${encodeURIComponent(model.id)}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          name: modelTitle,
          description: modelDescription,
          enabled,
          featuredScore,
          nodeType: selectedNodeType,
          category: selectedCategory,
          modelFamilyId: selectedModelFamilyId === "none" ? null : selectedModelFamilyId,
          purpose: selectedPurpose === "none" ? null : selectedPurpose,
          subPurpose: selectedSubPurpose === "none" ? null : selectedSubPurpose,
          parameterSchema: parsedSchema,
          parameterOverrides,
        }),
      });

      if (!response.ok) throw new Error("Failed to update model");

      const result = await response.json();
      const updatedModel = result.data.model;
      setModel(updatedModel);
      setParameterSchema(
        updatedModel.parameterSchema
          ? JSON.stringify(updatedModel.parameterSchema, null, 2)
          : "{}"
      );
      setHasChanges(false);

      toast.success("Model updated successfully!");
    } catch (error) {
      console.error("Error updating model:", error);
      toast.error("Failed to update model. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setShowCancelDialog(true);
    } else {
      navigate({ to: "/dashboard/model-management" });
    }
  };

  const handleCancelConfirm = () => {
    navigate({ to: "/dashboard/model-management" });
  };

  if (loading) {
    return (
      <div className="bg-background flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading model...</p>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="bg-background flex h-full items-center justify-center">
        <p className="text-muted-foreground">Model not found</p>
      </div>
    );
  }

  return (
    <div className="bg-background h-full overflow-auto">
      {/* Header */}
      <header>
        <div className="border-border flex h-10 shrink-0 items-center gap-2 border-b px-3 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-10 md:h-12 md:px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/dashboard" className="text-xs">Dashboard</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/dashboard/model-management" className="text-xs">Model Management</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-xs">{model.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="border-border flex flex-col border-b px-3 py-3 md:px-4 md:py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg font-bold tracking-tight md:text-xl mr-2">
                  {model.name}
                </h1>
                <div className="flex items-center gap-1.5 flex-wrap min-h-[24px]">
                  <Badge variant={model.enabled ? "default" : "secondary"} className="text-xs">
                    {model.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                  {model.modelFamily?.name && (
                    <Badge variant="outline" className="text-xs">
                      {model.modelFamily.name}
                    </Badge>
                  )}
                  {model.category && (
                    <Badge variant="outline" className="text-xs">
                      {getCategoryLabel(model.category)}
                    </Badge>
                  )}
                  {model.purpose && (
                    <Badge variant="outline" className="text-xs">
                      {getPurposeLabel(model.purpose)}
                    </Badge>
                  )}
                  {model.subPurpose && (
                    <Badge variant="outline" className="text-xs">
                      {getSubPurposeLabel(model.subPurpose)}
                    </Badge>
                  )}
                </div>
              </div>
              {model.description && (
                <p className="text-sm text-muted-foreground max-w-3xl">
                  {model.description}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={saving}
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-3 py-4 md:px-4">
        <div>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4 h-10">
              <TabsTrigger value="summary" className="text-sm gap-1.5">
                <Info className="h-4 w-4" />
                Summary
              </TabsTrigger>
              <TabsTrigger value="metadata" className="text-sm gap-1.5">
                <Layers className="h-4 w-4" />
                Metadata
              </TabsTrigger>
              <TabsTrigger value="params" className="text-sm gap-1.5">
                <Settings className="h-4 w-4" />
                Parameters
              </TabsTrigger>
              <TabsTrigger value="json" className="text-sm gap-1.5">
                <FileJson className="h-4 w-4" />
                JSON Schema
              </TabsTrigger>
            </TabsList>

            {/* Summary Tab */}
            <TabsContent value="summary">
              <SummaryTab model={model} providers={providers} />
            </TabsContent>

            {/* Metadata Tab */}
            <TabsContent value="metadata">
              <MetadataTab
                modelTitle={modelTitle}
                modelDescription={modelDescription}
                selectedNodeType={selectedNodeType}
                selectedCategory={selectedCategory}
                selectedModelFamilyId={selectedModelFamilyId}
                selectedPurpose={selectedPurpose}
                selectedSubPurpose={selectedSubPurpose}
                enabled={enabled}
                featuredScore={featuredScore}
                modelFamilies={modelFamilies}
                onTitleChange={setModelTitle}
                onDescriptionChange={setModelDescription}
                onNodeTypeChange={setSelectedNodeType}
                onCategoryChange={setSelectedCategory}
                onModelFamilyChange={setSelectedModelFamilyId}
                onPurposeChange={setSelectedPurpose}
                onSubPurposeChange={setSelectedSubPurpose}
                onEnabledChange={setEnabled}
                onScoreChange={setFeaturedScore}
              />
            </TabsContent>

            {/* Parameters Tab */}
            <TabsContent value="params">
              <ParametersTab
                model={model as { parameterSchema?: { properties?: Record<string, SchemaProperty>; required?: string[]; }; }}
                parameterOverrides={parameterOverrides}
                onOverridesChange={setParameterOverrides}
              />
            </TabsContent>

            {/* JSON Schema Tab */}
            <TabsContent value="json">
              <JsonSchemaTab
                parameterSchema={parameterSchema}
                onSchemaChange={setParameterSchema}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave this page? All changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, stay here</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelConfirm}>
              Yes, discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
